import React, { useEffect, useRef, useState } from "react";
import SignaturePad from "react-signature-canvas";
import axios from "axios";
import { FaCheckCircle } from "react-icons/fa";
import "../styles/Signature.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const SignatureManager = ({
  taskName, // legacy key (t_key)
  taskId = null, // preferred numeric ID
  userId,
  isAdmin,
  onSignComplete,
  referenceId = null, // e.g., employer ref for consent history
  strictReference = false,
  extraAnswers = {},
  autoReloadOnSign = true,
  onBeforeSign,
  refreshTrigger,
  requireStoredTaskSignature = false,
}) => {
  const sigPadRef = useRef(null);

  const [signatureURL, setSignatureURL] = useState(null);
  const [savedSignature, setSavedSignature] = useState(null);
  const [hasSignatureInput, setHasSignatureInput] = useState(false);
  const [isUsingDefaultSignature, setIsUsingDefaultSignature] = useState(false);
  const [isTaskSigned, setIsTaskSigned] = useState(false);
  const [signedDate, setSignedDate] = useState(null);

  // Load status + signatures for this user/task
  useEffect(() => {
    const fetchSignatures = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const idBase = taskId != null ? String(taskId) : null;
        const idKey =
          idBase == null
            ? null
            : referenceId != null
              ? `${idBase}_${referenceId}`
              : idBase;
        const nameKey = taskName
          ? referenceId != null
            ? `${taskName}_${referenceId}`
            : taskName
          : null;
        const requiresReferenceRecord =
          strictReference && referenceId == null;

        const taskSig =
          (!requiresReferenceRecord && idKey && res.data.signatures?.[idKey]) ??
          (!requiresReferenceRecord &&
            nameKey &&
            res.data.signatures?.[nameKey]) ??
          null;
        const taskSignedDate =
          (!requiresReferenceRecord && idKey && res.data.signdates?.[idKey]) ??
          (!requiresReferenceRecord &&
            nameKey &&
            res.data.signdates?.[nameKey]) ??
          null;

        const accountSignature = res.data.accountSignature || null;
        const latest = res.data.latestSignature || null;

        // A task signature and the user's reusable default are separate values.
        // Re-opening this task must never discard the reusable default.
        setSignatureURL(taskSig || null);
        setSavedSignature(accountSignature || latest || null);
        setSignedDate(taskSignedDate);

        const status =
          (!requiresReferenceRecord && idKey && res.data.status?.[idKey]) ??
          (!requiresReferenceRecord &&
            nameKey &&
            res.data.status?.[nameKey]) ??
          "not_completed";

        const hasSignedStatus =
          status === "review" ||
          status === "completed" ||
          status === "almost_expired";
        setIsTaskSigned(
          hasSignedStatus && (!requireStoredTaskSignature || Boolean(taskSig))
        );
        setHasSignatureInput(false);
        setIsUsingDefaultSignature(false);
      } catch (err) {
        console.error("Error loading signature:", err);
      }
    };

    fetchSignatures();
  }, [userId, taskId, taskName, referenceId, strictReference, refreshTrigger, requireStoredTaskSignature]); // intentionally not including API_BASE

  const handleSubmitSignature = async (overrideSig = null) => {
    try {
      if (onBeforeSign) {
        await onBeforeSign();
      }
    } catch (err) {
      const message =
        err?.message || "Please complete the task before signing.";
      alert(message);
      return;
    }

    const hasDrawnSignature =
      sigPadRef.current && !sigPadRef.current.isEmpty();
    const signatureData =
      overrideSig ||
      (hasDrawnSignature
        ? sigPadRef.current.getCanvas().toDataURL("image/png")
        : null);

    if (!signatureData) {
      alert("Please draw your signature or use your default signature first.");
      return;
    }

    try {
      const signResponse = await axios.post(
        `${API_BASE}/api/usertasks/sign`,
        {
          taskName,
          taskId,
          userId,
          signature: signatureData,
          replaceDefaultSignature:
            hasDrawnSignature && !isUsingDefaultSignature,
          referenceId,
          ...extraAnswers,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      setSignatureURL(signatureData);
      setSignedDate(signResponse.data?.signedDate || new Date().toISOString());
      if (signResponse.data?.defaultSignatureSaved) {
        setSavedSignature(signatureData);
      }
      setHasSignatureInput(false);
      setIsUsingDefaultSignature(false);

      if (autoReloadOnSign) {
        // verify then refresh
        try {
          const res = await axios.get(
            `${API_BASE}/api/usertasks/status/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
          const idBase = taskId != null ? String(taskId) : null;
          const idKey =
            idBase == null
              ? null
              : referenceId != null
                ? `${idBase}_${referenceId}`
                : idBase;
          const nameKey = taskName
            ? referenceId != null
              ? `${taskName}_${referenceId}`
              : taskName
            : null;
          const completed =
            ((idKey && res.data.status?.[idKey]) ??
              (nameKey && res.data.status?.[nameKey]) ??
              "not_completed") === "completed";
          if (completed) window.location.reload();
          else window.location.reload();
        } catch {
          window.location.reload();
        }
      } else if (onSignComplete) {
        try {
          const res = await axios.get(
            `${API_BASE}/api/usertasks/status/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
          const idBase = taskId != null ? String(taskId) : null;
          const idKey =
            idBase == null
              ? null
              : referenceId != null
                ? `${idBase}_${referenceId}`
                : idBase;
          const nameKey = taskName
            ? referenceId != null
              ? `${taskName}_${referenceId}`
              : taskName
            : null;
          const completed =
            ((idKey && res.data.status?.[idKey]) ??
              (nameKey && res.data.status?.[nameKey]) ??
              "not_completed") === "completed";
          onSignComplete(completed);
        } catch {
          onSignComplete(true);
        }
      }
    } catch (err) {
      console.error("Error saving signature:", err);
      alert("❌ Failed to save signature");
    }
  };

  const handleRedoSignature = async () => {
    try {
      await axios.post(
        `${API_BASE}/api/usertasks/reset`,
        { userId, taskName, taskId, referenceId },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      // ✅ Immediately reset local UI state
      setIsTaskSigned(false);
      setSignatureURL(null);
      setHasSignatureInput(false);
      setIsUsingDefaultSignature(false);
      setSignedDate(null);

      if (!autoReloadOnSign && onSignComplete) onSignComplete(false);

      // ✅ Auto-refresh the page so everything (status, dates, UI) syncs up
      window.location.reload();
    } catch (err) {
      console.error("❌ Reset signature failed:", err);
      alert("Failed to reset signature.");
    }
  };

  // --- Render ---
  if (isTaskSigned) {
    const imgSrc = signatureURL || savedSignature || null;
    return (
      <div className="mt-4">
        <label className="form-label">Signature:</label>
        <div className="signature-container" data-download-media-frame>
          {imgSrc && (
            <img
              src={imgSrc}
              alt="Signature"
              className="signature-image"
              data-download-preserve-ratio
            />
          )}
          {signedDate && (
            <small className="text-muted mt-2">
              Signed {new Date(signedDate).toLocaleString()}
            </small>
          )}
        </div>
        {!isAdmin && (
          <div
            className="task-signature-status mt-3"
            role="status"
            data-download-exclude
          >
            <span className="task-signature-status-icon" aria-hidden="true">
              <FaCheckCircle />
            </span>
            <span className="task-signature-status-content">
              <strong>Signature complete</strong>
              <span>
                This form has already been signed. No further action is required.
              </span>
            </span>
          </div>
        )}
        {isAdmin && (
          <button
            type="button"
            className="btn btn-secondary mt-2"
            onClick={handleRedoSignature}
            data-download-exclude
          >
            Re-do Signature
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <label className="form-label">Signature:</label>
      <div className="signature-container" data-download-media-frame>
        <SignaturePad
          ref={sigPadRef}
          onEnd={() => {
            setHasSignatureInput(
              Boolean(sigPadRef.current && !sigPadRef.current.isEmpty()),
            );
            setIsUsingDefaultSignature(false);
          }}
          canvasProps={{
            className: "sigCanvas",
            "data-download-preserve-ratio": true,
          }}
        />
        <div className="signature-buttons mt-2" data-download-exclude>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              if (!hasSignatureInput) {
                if (!savedSignature) {
                  alert("No default signature is saved in My Account.");
                  return;
                }

                sigPadRef.current?.fromDataURL(savedSignature);
                setHasSignatureInput(true);
                setIsUsingDefaultSignature(true);
                return;
              }

              sigPadRef.current?.clear();
              setHasSignatureInput(false);
              setIsUsingDefaultSignature(false);
            }}
          >
            {!hasSignatureInput ? "Use Default Signature" : "Clear Signature"}
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={() => handleSubmitSignature()}
          >
            Save Signature &amp; Complete
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureManager;
